import React, { useEffect, useMemo, useState } from 'react';
import {
  Flame,
  ChevronLeft,
  ChevronRight,
  Search,
  Loader2,
  AlertCircle,
  Users,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Input, Select, Badge } from '../../Components/ui';
import { API_URL } from '../../api/config';

// Mesma lista fixa de dias/turnos já usada nos filtros de Ajuste Grade
// (frontend/src/AjusteGrade.js) — matrículas desta escola só existem de
// segunda a sexta, por isso não inclui sábado/domingo aqui.
const DIAS_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const TURNOS = ['Manhã', 'Tarde', 'Noite'];
// Colunas fixas da fileira de chamas — sempre Segunda a Sexta, na mesma
// ordem/posição pra todo mundo, mesmo que o aluno só venha em alguns desses
// dias (ver renderização abaixo: dia sem aula esperada vira espaço vazio na
// coluna dele, não é omitido).
const COLUNAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex'];

const normalizar = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Formata "2026-09-14" -> "14/09" pro cabeçalho da semana, sem depender de
// fuso (mesmo cuidado de sempre usar T00:00:00 ao montar Date a partir de
// uma string de data pura, ver hojeBrasil() nos outros lugares do sistema).
const formatarCurto = (iso) => {
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
};

const somarDias = (iso, dias) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toISOString().split('T')[0];
};

// Tela "Foguinhos" — a mesma fileira de chamas que o próprio aluno já vê no
// perfil dele (ver AlunoPerfil.js), só que aqui pra TODOS os alunos da
// instituição de uma vez, com navegação entre semanas. Liberada pra
// QUALQUER usuário logado de propósito (ver comentário em backend/telas.js e
// o registro da rota em App.js/Layout.jsx — não passa pelo sistema
// configurável de Permissões, igual a "Trocar Senha").
function Foguinhos() {
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const [dataRef, setDataRef] = useState(null); // null = semana atual (backend decide)
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [professorFiltro, setProfessorFiltro] = useState('');
  const [diaFiltro, setDiaFiltro] = useState('');
  const [turnoFiltro, setTurnoFiltro] = useState('');

  const [professores, setProfessores] = useState([]);
  useEffect(() => {
    if (!institutionId || !token) return;
    fetch(`${API_URL}/api/professores`, {
      headers: { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((body) => {
        const data = body?.data ?? (Array.isArray(body) ? body : []);
        setProfessores(Array.isArray(data) ? data : []);
      })
      .catch(() => setProfessores([]));
  }, [institutionId, token]);

  useEffect(() => {
    if (!institutionId || !token) return;
    let cancelado = false;
    setLoading(true);
    setErro('');
    const url = new URL(`${API_URL}/api/foguinhos`);
    if (dataRef) url.searchParams.set('data', dataRef);
    if (professorFiltro) url.searchParams.set('professor', professorFiltro);
    if (diaFiltro) url.searchParams.set('dia_semana', diaFiltro);
    if (turnoFiltro) url.searchParams.set('turno', turnoFiltro);
    fetch(url, { headers: { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Erro ao carregar foguinhos.');
        if (!cancelado) setDados(data?.data ?? data);
      })
      .catch((err) => {
        if (!cancelado) setErro(err.message);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [institutionId, token, dataRef, professorFiltro, diaFiltro, turnoFiltro]);

  const filtrosAtivos = !!(professorFiltro || diaFiltro || turnoFiltro);
  const limparFiltros = () => {
    setProfessorFiltro('');
    setDiaFiltro('');
    setTurnoFiltro('');
  };

  const buscaNorm = normalizar(busca.trim());
  const alunosFiltrados = useMemo(() => {
    if (!dados) return [];
    if (!buscaNorm) return dados.alunos;
    return dados.alunos.filter((a) => normalizar(a.nome).includes(buscaNorm));
  }, [dados, buscaNorm]);

  const irParaSemanaAtual = () => setDataRef(null);
  const semanaAnterior = () => setDataRef(somarDias(dados.inicio_semana, -7));
  const proximaSemana = () => setDataRef(somarDias(dados.inicio_semana, 7));

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shadow-sm shrink-0">
            <Flame className="w-5 h-5 text-white" fill="currentColor" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Presença Semanal
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Sequência de presença da semana de cada inscrito.
            </p>
          </div>
        </div>

        {erro && (
          <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-700 dark:text-red-400 text-sm flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {erro}
          </div>
        )}

        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={semanaAnterior}
                disabled={!dados}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors disabled:opacity-40"
                title="Semana anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => dados && irParaSemanaAtual()}
                disabled={!dados}
                className="px-3 h-8 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40"
                title="Voltar pra semana atual"
              >
                {dados
                  ? `${formatarCurto(dados.inicio_semana)} – ${formatarCurto(dados.fim_semana)}`
                  : '...'}
              </button>
              <button
                type="button"
                onClick={proximaSemana}
                disabled={!dados}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors disabled:opacity-40"
                title="Próxima semana"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="w-full sm:w-64">
              <Input
                placeholder="Buscar inscrito..."
                icon={Search}
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                size="sm"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <div className="w-full sm:w-48">
              <Select
                size="sm"
                value={professorFiltro}
                onChange={(e) => setProfessorFiltro(e.target.value)}
                options={[
                  { value: '', label: 'Todos os educadores' },
                  ...professores.map((p) => ({ value: p, label: p })),
                ]}
              />
            </div>
            <div className="w-full sm:w-40">
              <Select
                size="sm"
                value={diaFiltro}
                onChange={(e) => setDiaFiltro(e.target.value)}
                options={[
                  { value: '', label: 'Todos os dias' },
                  ...DIAS_SEMANA.map((d) => ({ value: d, label: d })),
                ]}
              />
            </div>
            <div className="w-full sm:w-36">
              <Select
                size="sm"
                value={turnoFiltro}
                onChange={(e) => setTurnoFiltro(e.target.value)}
                options={[
                  { value: '', label: 'Todos os turnos' },
                  ...TURNOS.map((t) => ({ value: t, label: t })),
                ]}
              />
            </div>
            {filtrosAtivos && (
              <button
                type="button"
                onClick={limparFiltros}
                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              >
                Limpar filtros
              </button>
            )}
          </div>
        </Card>

        <Card padding="none">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Inscritos</h2>
            {dados && (
              <Badge tone="neutral">
                {alunosFiltrados.length} de {dados.alunos.length}
              </Badge>
            )}
          </div>
          <div className="p-3">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-3" />
                <span className="text-sm">Carregando...</span>
              </div>
            ) : alunosFiltrados.length === 0 ? (
              <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">
                {busca
                  ? 'Nenhum inscrito encontrado para essa busca.'
                  : 'Nenhum inscrito ativo nesta instituição.'}
              </div>
            ) : (
              <div className="space-y-2">
                {alunosFiltrados.map((aluno) => (
                  <div
                    key={aluno.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-2.5 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/40"
                  >
                    <div className="min-w-0">
                      <p
                        className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                        translate="no"
                      >
                        {aluno.nome}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {[aluno.turno, aluno.turma].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {COLUNAS_SEMANA.map((diaCurto) => {
                        const dia = aluno.dias.find((d) => d.dia_semana_curto === diaCurto);
                        if (!dia) {
                          // Aluno não tem aula esperada nesse dia da semana —
                          // mantém a coluna (espaço vazio) pra Segunda/Terça/
                          // etc. sempre alinhar na mesma posição entre todos
                          // os inscritos da lista, independente de quantos
                          // dias cada um vem.
                          return (
                            <div
                              key={diaCurto}
                              className="flex flex-col items-center gap-1 opacity-30"
                            >
                              <span className="text-[9px] font-medium text-gray-400 dark:text-gray-500">
                                {diaCurto}
                              </span>
                              <div className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                                <div className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
                              </div>
                            </div>
                          );
                        }
                        const ehPresente = dia.estado === 'presente';
                        const ehFalta = dia.estado === 'falta';
                        return (
                          <div key={diaCurto} className="flex flex-col items-center gap-1">
                            <span className="text-[9px] font-medium text-gray-400 dark:text-gray-500">
                              {diaCurto}
                            </span>
                            <div
                              className={`relative w-7 h-7 rounded-full flex items-center justify-center ${ehPresente ? 'bg-orange-50 dark:bg-orange-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}
                            >
                              <Flame
                                className={`w-3.5 h-3.5 ${ehPresente ? 'text-orange-500' : 'text-gray-300 dark:text-gray-600'}`}
                                fill={ehPresente ? 'currentColor' : 'none'}
                              />
                              {ehFalta && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-5 h-0.5 bg-red-400 rotate-45 rounded-full" />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </main>
    </div>
  );
}

export default Foguinhos;
